import { z } from 'zod';

/**
 * Allowed values for the deployment-tier env (`VITE_APP_ENV`). This is the
 * authoritative source of the env literal union — `vite-env.d.ts` mirrors it.
 */
export const APP_ENV_VALUES = ['development', 'dev', 'qa', 'uat', 'production', 'sandbox'] as const;

export type AppEnv = (typeof APP_ENV_VALUES)[number];

/** Tiers that run as a deployed environment (everything except local development). */
const DEPLOYED_TIERS: ReadonlySet<string> = new Set<string>(['dev', 'qa', 'uat', 'production', 'sandbox']);

const DEFAULT_FILE_UPLOAD_SIZE_LIMIT = 100000000;

/**
 * A raw env source — `import.meta.env` at runtime, or a plain record in tests.
 * Vite exposes every value as a string (or undefined when absent).
 */
export type RawEnvSource = Record<string, string | undefined>;

/**
 * Coerce the file-upload size limit, guarding against NaN. An unset value
 * falls back to the historical default; a present-but-non-numeric value is a
 * misconfiguration and is reported as a validation failure.
 */
const fileUploadSizeLimitSchema = z
  .string()
  .optional()
  .transform((value, ctx) => {
    if (value === undefined || value === '') {
      return DEFAULT_FILE_UPLOAD_SIZE_LIMIT;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'must be a number' });
      return z.NEVER;
    }
    return parsed;
  });

const requiredString = z.string().trim().min(1);

/**
 * Zod schema over the raw `VITE_APP_*` keys. Keys are validated under their
 * source names so error messages point at the exact env var to fix, then
 * mapped to friendly names in {@link validateEnv}.
 */
const rawEnvSchema = z.object({
  VITE_APP_EXPRESS_URL: requiredString,
  VITE_APP_NESTJS_BASE_URL: requiredString,
  VITE_APP_AUTH0_DOMAIN: requiredString,
  VITE_APP_AUTH0_CLIENT_ID: requiredString,
  VITE_APP_AUTH0_AUDIENCE: requiredString,
  VITE_APP_ENV: z.enum(APP_ENV_VALUES),
  VITE_APP_FILE_UPLOAD_SIZE_LIMIT: fileUploadSizeLimitSchema
});

export interface ValidatedEnv {
  EXPRESS_URL: string;
  NESTJS_BASE_URL: string;
  AUTH0_DOMAIN: string;
  AUTH0_CLIENT_ID: string;
  AUTH0_AUDIENCE: string;
  ENV: AppEnv;
  FILE_UPLOAD_SIZE_LIMIT: number;
}

/**
 * Validate the required runtime env, throwing a single aggregated error that
 * names every offending `VITE_APP_*` var. Call once at boot (see
 * `src/index.tsx`) so a misconfigured build fails fast instead of rendering a
 * broken or blank app.
 * @param {RawEnvSource} source - Raw env record (defaults to `import.meta.env`).
 * @returns {ValidatedEnv} The validated, coerced env values.
 */
export const validateEnv = (source: RawEnvSource = import.meta.env): ValidatedEnv => {
  const result = rawEnvSchema.safeParse(source);
  if (!result.success) {
    const details = result.error.issues
      .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
      .join('; ');
    throw new Error(`Invalid environment configuration: ${details}`);
  }
  const parsed = result.data;
  return {
    EXPRESS_URL: parsed.VITE_APP_EXPRESS_URL,
    NESTJS_BASE_URL: parsed.VITE_APP_NESTJS_BASE_URL,
    AUTH0_DOMAIN: parsed.VITE_APP_AUTH0_DOMAIN,
    AUTH0_CLIENT_ID: parsed.VITE_APP_AUTH0_CLIENT_ID,
    AUTH0_AUDIENCE: parsed.VITE_APP_AUTH0_AUDIENCE,
    ENV: parsed.VITE_APP_ENV,
    FILE_UPLOAD_SIZE_LIMIT: parsed.VITE_APP_FILE_UPLOAD_SIZE_LIMIT
  };
};

/**
 * Whether the current deploy tier should initialize deployed-only integrations
 * (LogRocket, etc.). Local `development` is excluded.
 * @param {string | undefined} env - The deploy tier (VITE_APP_ENV).
 * @returns {boolean} True when running on a deployed tier.
 */
export const isDeployedTier = (env: string | undefined): boolean => env !== undefined && DEPLOYED_TIERS.has(env);

/**
 * Coerce the file-upload size limit from a raw value without throwing — used by
 * lenient module-level consumers (`shared/constants.ts`) that must not crash on
 * import in environments where strict validation hasn't run.
 * @param {string | undefined} value - Raw env value.
 * @returns {number} The parsed limit, or the default when absent/invalid.
 */
export const parseFileUploadSizeLimit = (value: string | undefined): number => {
  if (value === undefined || value === '') {
    return DEFAULT_FILE_UPLOAD_SIZE_LIMIT;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? DEFAULT_FILE_UPLOAD_SIZE_LIMIT : parsed;
};
